/**
 * Encodes a string into a Base64-encoded string.
 * Performs validation and handles errors gracefully.
 *
 * @param inputString - The string to encode in Base64.
 * @returns The Base64-encoded string.
 */
export function encodeBase64(inputString: string): string {
    try {
        // Validate if the input is a non-empty string
        if (typeof inputString !== 'string' || inputString.trim() === '') {
            throw new Error('Input must be a non-empty string.');
        }

        // Encode the string to Base64
        const encoded = btoa(inputString); // Encodes the string to Base64

        return encoded;
    } catch (error) {
        // Handle errors
        throw new Error(
            `Failed to encode string to Base64: ${(error as Error).message}`,
        );
    }
}

/**
 * Decodes a Base64-encoded string.
 * Performs validation and handles errors gracefully.
 *
 * @param base64String - The Base64-encoded string to decode.
 * @returns The decoded string if valid, or throws an error if invalid.
 */
export function decodeBase64(base64String: string): string {
    try {
        // Validate if the input is a non-empty string
        if (typeof base64String !== 'string' || base64String.trim() === '') {
            throw new Error('Input must be a non-empty Base64-encoded string.');
        }

        // Validate Base64 string format using a regex
        const base64Regex =
            /^(?:[A-Za-z0-9+/]{4})*(?:[A-Za-z0-9+/]{2}==|[A-Za-z0-9+/]{3}=)?$/;
        if (!base64Regex.test(base64String)) {
            throw new Error('Invalid Base64 string format.');
        }

        // Attempt decoding
        const decoded = atob(base64String); // Decodes the Base64 string

        return decoded;
    } catch (error) {
        // Handle any decoding or validation errors
        throw new Error(
            `Failed to decode Base64 string: ${(error as Error).message}`,
        );
    }
}
